import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';

import { Input } from '../components/ui/atoms/Input';
import { Label } from '../components/ui/label';
import { Button } from '@/components/ui/atoms/Button';
import { Loading } from '../components/Loading';
import {
  AuthClientError,
  authReturnPath,
  finalizeOnboarding,
  GOOGLE_CONNECTION,
  loginEmailPassword,
  MICROSOFT_CONNECTION,
  signupEmailPassword,
  signupIntent,
  type SocialConnection,
} from '../services/auth.service';
import { getPlanByCode } from '../config/plans';
import { getLocalizedPlanByCode } from '../components/landing/landingContent';
import { GoogleIcon, MicrosoftIcon } from '../components/auth/SocialIcons';
import { AuthLayout } from '../components/auth/AuthLayout';
import { submitKickerLead } from '../components/landing/KickerCapture';
import { isBlockedSignupEmail, validateWorkEmail } from '../utils/workEmail';
import { formatPrice, normalizeCurrency } from '../utils/currency';

const MIN_PASSWORD = 12;

export const SignupPage: React.FC = () => {
  const { t } = useTranslation(['auth', 'common']);
  const { t: tMarketing } = useTranslation('marketing');
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const {
    isAuthenticated,
    isLoading: authLoading,
    getAccessTokenSilently,
    loginWithRedirect,
  } = useAuth0();

  const urlPlan = params.get('plan') || 'free_trial';
  const urlCurrency = params.get('currency') || undefined;
  const invitationToken = params.get('invitation') || undefined;
  const prefilledEmail = params.get('email') || '';

  const [invitation, setInvitation] = useState<{
    email: string;
    tenant_name: string | null;
    assigned_plan_code: string | null;
  } | null>(null);

  // For invitations, the canonical plan code comes from the invitation
  // record on the backend (the URL token is opaque). For self-service,
  // it comes straight from the URL.
  const planCode = invitation?.assigned_plan_code || urlPlan;
  const plan = useMemo(() => getPlanByCode(planCode), [planCode]);
  const localizedPlan = useMemo(
    () => getLocalizedPlanByCode(tMarketing, planCode),
    [tMarketing, planCode],
  );

  const [email, setEmail] = useState(prefilledEmail);
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [autoFinalizing, setAutoFinalizing] = useState(false);
  const autoFinalizeRan = useRef(false);

  // Fetch the invitation context once when an invitation token is in the
  // URL — gives us the actual pack code and the inviting workspace name
  // to render correct copy on the left pane.
  useEffect(() => {
    if (!invitationToken) {
      setInvitation(null);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const apiUrl = import.meta.env.VITE_API_SERVER_URL as string;
        const resp = await fetch(
          `${apiUrl}/api/invitations/validate/${encodeURIComponent(invitationToken)}`,
        );
        if (!resp.ok) return;
        const data = await resp.json();
        if (cancelled) return;
        setInvitation({
          email: data.email,
          tenant_name: data.tenant_name ?? null,
          assigned_plan_code: data.assigned_plan_code ?? null,
        });
        if (data.email && !prefilledEmail) {
          setEmail(data.email);
        }
      } catch {
        // Non-fatal — page still renders with URL fallbacks.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [invitationToken, prefilledEmail]);

  useEffect(() => {
    signupIntent.set({
      plan: planCode,
      currency: urlCurrency,
      invitation_token: invitationToken,
    });
  }, [planCode, urlCurrency, invitationToken]);

  // Already authenticated visitor — they completed signup before but
  // bailed out of plan selection (e.g. a previous PAYG attempt 404'd).
  // Skip the form, claim the URL's plan via finalize, and route them.
  useEffect(() => {
    if (authLoading || !isAuthenticated || autoFinalizeRan.current) return;
    autoFinalizeRan.current = true;
    setAutoFinalizing(true);
    (async () => {
      try {
        const token = await getAccessTokenSilently();
        const result = await finalizeOnboarding({
          accessToken: token,
          plan_code: planCode,
          currency: urlCurrency,
          invitation_token: invitationToken,
        });
        signupIntent.clear();
        if (result.next_action === 'checkout' && result.checkout_url) {
          window.location.assign(result.checkout_url);
          return;
        }
        if (result.next_action === 'setup_card' && result.redirect_path) {
          navigate(result.redirect_path, { replace: true });
          return;
        }
        navigate('/dashboard', { replace: true });
      } catch (e) {
        const msg = e instanceof Error ? e.message : 'Could not finalize plan';
        toast.error(msg);
        navigate('/dashboard', { replace: true });
      }
    })();
  }, [authLoading, isAuthenticated, planCode, urlCurrency, invitationToken, getAccessTokenSilently, navigate]);

  if (authLoading || autoFinalizing) {
    return <Loading />;
  }

  const passwordValid = password.length >= MIN_PASSWORD;
  // Invitations come pre-validated by the inviting admin, so we don't
  // re-check the domain — they may legitimately invite a personal email.
  const enforceWorkEmail = !invitationToken;
  const emailIsBlocked = enforceWorkEmail && !!email && isBlockedSignupEmail(email);
  const formValid = email.trim().length > 3 && passwordValid && !emailIsBlocked;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formValid || submitting) return;
    setError(null);
    if (enforceWorkEmail) {
      const emailError = validateWorkEmail(email);
      if (emailError) {
        setError(emailError);
        toast.error(emailError);
        return;
      }
    }
    submitKickerLead(email);
    setSubmitting(true);
    try {
      await signupEmailPassword({ email, password });
      const ticket = await loginEmailPassword({ email, password });
      await loginWithRedirect({
        appState: { returnTo: '/auth-callback' },
        authorizationParams: {
          login_ticket: ticket.login_ticket,
        },
      });
    } catch (err) {
      const msg = err instanceof AuthClientError ? err.message : t('auth:signup.signup_failed');
      setError(msg);
      toast.error(msg);
      setSubmitting(false);
    }
  };

  const handleSocial = async (connection: SocialConnection) => {
    signupIntent.set({ plan: planCode, currency: urlCurrency, invitation_token: invitationToken });
    // Save the current page so /auth-callback can redirect back here if
    // Auth0 returns an error (e.g. personal_email_blocked Post-Login Action).
    authReturnPath.save(window.location.pathname + window.location.search);
    // Hand-rolling the /authorize redirect breaks the SDK's state check
    // on the way back (it only trusts state values it generated). Going
    // through loginWithRedirect lets the SDK manage state/nonce/code
    // exchange end-to-end.
    await loginWithRedirect({
      appState: { returnTo: '/auth-callback' },
      authorizationParams: { connection },
    });
  };

  // The backend auto-generates placeholder workspace names in three
  // shapes — never expose any of them in the UI:
  //   1. "Workspace for {email}" — admin invitation with no tenant
  //   2. "tenant-{uuid}"          — final fallback in invitation_service
  //   3. an email address           — self-service when user.company is unset
  //                                  (`tenant_name = user.company or user.email`)
  const isPlaceholderName = (name: string | null | undefined): boolean => {
    if (!name) return true;
    if (/^Workspace for /i.test(name)) return true;
    if (/^tenant-/i.test(name)) return true;
    // Loose email check — anything with "@" + "." after it is an email.
    if (/^\S+@\S+\.\S+$/.test(name.trim())) return true;
    return false;
  };
  const friendlyWorkspaceName =
    invitation?.tenant_name && !isPlaceholderName(invitation.tenant_name)
      ? invitation.tenant_name
      : null;

  // Plan names already end in "Pack" for some packs (e.g. "Core Pack");
  // the wrapping copy adds "pack" itself in some places, which double-
  // words. Strip the trailing word to keep things clean.

  const planTitle = invitationToken
    ? friendlyWorkspaceName
      ? t('auth:signup.plan_title_invite_workspace', { workspace: friendlyWorkspaceName })
      : t('auth:signup.plan_title_invite_default')
    : t('auth:signup.plan_title_default');

  const planTagline = invitationToken
    ? t('auth:signup.plan_tagline_invite')
    : localizedPlan?.planName ?? plan?.planName ?? t('auth:signup.plan_tagline_fallback');

  const isPaidPlan = !!plan && plan.baseAmount > 0;
  const description = invitationToken
    ? friendlyWorkspaceName
      ? t('auth:signup.plan_description_invite_workspace', { workspace: friendlyWorkspaceName })
      : t('auth:signup.plan_description_invite_default')
    : isPaidPlan
      ? t('auth:signup.plan_description_paid')
      : t('auth:signup.plan_description_free');

  const INVITATION_VALUE_PROPS = [
    { text: t('auth:signup.inv_value_prop_1') },
    { text: t('auth:signup.inv_value_prop_2') },
    { text: t('auth:signup.inv_value_prop_3') },
    { text: t('auth:signup.inv_value_prop_4') },
  ];

  const features = invitationToken
    ? INVITATION_VALUE_PROPS
    : localizedPlan?.features ?? plan?.features?.slice(0, 5) ?? [
        { text: t('auth:signup.value_prop_ai') },
        { text: t('auth:signup.value_prop_interviews') },
        { text: t('auth:signup.value_prop_reports') },
        { text: t('auth:signup.value_prop_setup') },
      ];

  // For invitations: never show price (the inviter is paying). For
  // self-service: show only for paid plans (free_trial would duplicate
  // the eyebrow tag).
  const showPrice = !!plan && plan.baseAmount > 0 && !invitationToken;

  // Price label respects the currency the user picked on the landing
  // page — MAD users see MAD pricing, EUR users see EUR, etc. We pass
  // eurAmount when present so EUR shows the curated round number
  // (260 €) instead of a conversion (262 €).
  const currency = normalizeCurrency(urlCurrency);
  const formattedPrice = showPrice
    ? formatPrice(plan!.baseAmount, currency, plan!.eurAmount)
    : undefined;

  return (
    <AuthLayout
      planTitle={planTitle}
      planTagline={planTagline}
      planFeatures={features}
      description={description}
      planPriceLabel={formattedPrice}
      planPriceSubtext={showPrice ? localizedPlan?.priceSubtext ?? plan?.priceSubtext : undefined}
      showMobilePlanSummary={!invitationToken}
    >
      <div className="space-y-6">
        {invitationToken && (
          <div className="rounded-lg border border-purple/30 bg-purple/5 px-4 py-3 text-sm text-dark-invirtus">
            <span className="font-semibold">{t('auth:signup.invited_banner_label')}</span>
            {friendlyWorkspaceName ? (
              <> {t('auth:signup.invited_to', { workspace: friendlyWorkspaceName })}</>
            ) : (
              <> {t('auth:signup.invited_to_workspace')}</>
            )}
          </div>
        )}
        <div>
          <h1 className="text-2xl font-bold text-dark-invirtus">
            {t('auth:signup.title')}
          </h1>
          <p className="mt-1 text-sm text-dark-invirtus/60">
            {invitationToken
              ? t('auth:signup.subtitle_invitation')
              : t('auth:signup.subtitle_default')}
          </p>
        </div>

        <div className="space-y-3">
          <Button
            type="outlined"
            intent="muted"
            size="md"
            label={t('auth:signup.continue_google')}
            icon={<GoogleIcon />}
            className="w-full"
            onClick={() => handleSocial(GOOGLE_CONNECTION)}
          />
          <Button
            type="outlined"
            intent="muted"
            size="md"
            label={t('auth:signup.continue_microsoft')}
            icon={<MicrosoftIcon />}
            className="w-full"
            onClick={() => handleSocial(MICROSOFT_CONNECTION)}
          />
        </div>

        <div className="relative">
          <div className="absolute inset-0 flex items-center">
            <div className="w-full border-t border-light-lila" />
          </div>
          <div className="relative flex justify-center text-xs">
            <span className="bg-white px-3 text-dark-invirtus/50">
              {t('auth:signup.or_with_email')}
            </span>
          </div>
        </div>

        <form className="space-y-4" onSubmit={handleSubmit} noValidate>
          <div className="space-y-1.5">
            <Label htmlFor="signup-email" className="text-dark-invirtus">
              {t('common:forms.work_email')}
            </Label>
            <Input
              id="signup-email"
              type="email"
              autoComplete="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder={t('common:forms.email_placeholder')}
              rounded="lg"
              readOnly={!!invitation?.email}
              className={invitation?.email ? 'bg-light-lin/40 cursor-not-allowed' : ''}
            />
            {invitation?.email ? (
              <p className="text-xs text-dark-invirtus/50">
                {t('auth:signup.email_locked')}
              </p>
            ) : enforceWorkEmail && emailIsBlocked ? (
              <p className="text-xs text-red-600">
                {t('auth:signup.personal_email_error')}
              </p>
            ) : null}
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="signup-password" className="text-dark-invirtus">
              {t('common:forms.password')}
            </Label>
            <Input
              id="signup-password"
              type="password"
              autoComplete="new-password"
              required
              minLength={MIN_PASSWORD}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder={t('auth:signup.password_hint')}
              rounded="lg"
            />
            {password.length > 0 && !passwordValid && (
              <p className="text-xs text-dark-invirtus/60">
                {t('auth:signup.password_weak')}
              </p>
            )}
          </div>

          {error && (
            <p className="text-sm text-red-600" role="alert">
              {error}
            </p>
          )}

          <Button
            label={submitting ? t('auth:signup.creating') : t('auth:signup.create')}
            type="contained"
            intent="secondary"
            htmlType="submit"
            disabled={!formValid || submitting}
            loading={submitting}
            className="sticky bottom-4 w-full"
            style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
          />
        </form>

        <p className="text-center text-sm text-dark-invirtus/60">
          {t('auth:signup.already_have_account')}{' '}
          <Link to="/login" className="font-medium text-purple hover:underline">
            {t('auth:signup.sign_in')}
          </Link>
        </p>

        <p className="text-center text-xs text-dark-invirtus/40">
          {t('auth:signup.terms_prefix')}{' '}
          <Link to="/terms" className="underline">{t('auth:signup.terms_link')}</Link>{' '}
          {t('auth:signup.and')}{' '}
          <Link to="/privacy" className="underline">{t('auth:signup.privacy_link')}</Link>.
        </p>
      </div>
    </AuthLayout>
  );
};

export default SignupPage;
